/**
 * useOKRData Hook
 *
 * Manages all OKR data fetching and primary state
 */

import { useState, useCallback, useEffect } from 'react';
import httpClient from '@utils/httpClient';
import { okrApi } from '../../../services/okrApi';
import {
    UserContext,
    Note,
    OrgObjective,
    TeamObjective,
    IndividualObjective,
    Period
} from '../types';

const DEFAULT_USER_CONTEXT: UserContext = {
    id: '',
    role: 'Employee',
    dept_id: null,
    is_manager: false,
    name: ''
};

export interface UseOKRDataReturn {
    // Loading & Error States
    loading: boolean;
    error: string | null;
    success: string | null;
    setError: (error: string | null) => void;
    setSuccess: (success: string | null) => void;

    // User Context
    userContext: UserContext;
    setUserContext: React.Dispatch<React.SetStateAction<UserContext>>;

    // Period Data
    periods: Period[];
    activePeriod: Period | null;
    viewedPeriod: Period | null;
    selectedPeriodId: string;
    setSelectedPeriodId: (id: string) => void;
    isLocked: boolean;
    setIsLocked: (locked: boolean) => void;

    // OKR Data
    orgObjectives: OrgObjective[];
    setOrgObjectives: React.Dispatch<React.SetStateAction<OrgObjective[]>>;
    teamObjectives: TeamObjective[];
    setTeamObjectives: React.Dispatch<React.SetStateAction<TeamObjective[]>>;
    myObjectives: IndividualObjective[];
    setMyObjectives: React.Dispatch<React.SetStateAction<IndividualObjective[]>>;

    // Supporting Data
    atomicMapData: any;
    setAtomicMapData: React.Dispatch<React.SetStateAction<any>>;
    departments: any[];
    setDepartments: React.Dispatch<React.SetStateAction<any[]>>;
    employees: any[];
    setEmployees: React.Dispatch<React.SetStateAction<any[]>>;
    notes: Note[];
    setNotes: React.Dispatch<React.SetStateAction<Note[]>>;

    // Seeding State
    seedingPeriods: boolean;
    setSeedingPeriods: (seeding: boolean) => void;

    // Actions
    fetchInitialData: () => Promise<void>;
    handleSeedPeriods: () => Promise<void>;
    handleViewPeriod: (periodId: string) => Promise<void>;
    handleSetActivePeriod: (periodId: string) => Promise<void>;
    handleLockToggle: () => Promise<void>;
    refetchData: () => Promise<void>;
    softRefetchData: () => Promise<void>;
}

export const useOKRData = (): UseOKRDataReturn => {
    // Loading & Error States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // User Context
    const [userContext, setUserContext] = useState<UserContext>(DEFAULT_USER_CONTEXT);

    // Period Data
    const [periods, setPeriods] = useState<Period[]>([]);
    const [activePeriod, setActivePeriod] = useState<Period | null>(null);
    const [viewedPeriod, setViewedPeriod] = useState<Period | null>(null);
    const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
    const [isLocked, setIsLocked] = useState(false);
    const [seedingPeriods, setSeedingPeriods] = useState(false);

    // OKR Data
    const [orgObjectives, setOrgObjectives] = useState<OrgObjective[]>([]);
    const [teamObjectives, setTeamObjectives] = useState<TeamObjective[]>([]);
    const [myObjectives, setMyObjectives] = useState<IndividualObjective[]>([]);

    // Supporting Data
    const [atomicMapData, setAtomicMapData] = useState<any>(null);
    const [departments, setDepartments] = useState<any[]>([]);
    const [employees, setEmployees] = useState<any[]>([]);
    const [notes, setNotes] = useState<Note[]>([]);

    // Auto-clear success/error messages
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => setError(null), 8000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const fetchInitialData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch fresh user context from API (ensures is_manager & dept_id are current)
            try {
                const profileResponse = await httpClient.get<{ success: boolean; user: any }>('/auth/profile');
                const freshUser = profileResponse.user;
                console.log('👤 User from /auth/profile:', freshUser);

                // Update localStorage with fresh data so other modules stay in sync
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const mergedUser = { ...storedUser, ...freshUser };
                localStorage.setItem('user', JSON.stringify(mergedUser));

                setUserContext({
                    id: freshUser.id || '',
                    role: freshUser.role || 'Employee',
                    dept_id: freshUser.department_id || freshUser.dept_id || null,
                    is_manager: freshUser.is_manager === 1 || freshUser.is_manager === true || false,
                    name: freshUser.name || ''
                });
            } catch (profileErr) {
                console.warn('Failed to fetch profile, falling back to localStorage:', profileErr);
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    const user = JSON.parse(storedUser);
                    setUserContext({
                        id: user.id || '',
                        role: user.role || 'Employee',
                        dept_id: user.dept_id || user.department_id || null,
                        is_manager: user.is_manager === 1 || user.is_manager === true || false,
                        name: user.name || user.firstName || ''
                    });
                }
            }

            // Fetch periods
            let periodsData: Period[] = [];
            try {
                const periodsResponse = await okrApi.getPeriods();
                console.log('📅 Periods response:', periodsResponse);
                periodsData = Array.isArray(periodsResponse) ? periodsResponse : [];
                console.log('📅 Periods array:', periodsData);
                setPeriods(periodsData);
            } catch (err) {
                console.error('Error fetching periods:', err);
                setPeriods([]);
            }

            // Fetch active period
            let active: any = null;
            try {
                const activeResponse = await okrApi.getActivePeriod();
                console.log('🔴 Active period API response:', activeResponse);
                active = activeResponse || null;
                console.log('🔴 Active period parsed object:', active);
                if (active) {
                    console.log('🔴 Active period ID:', active.id);
                    console.log('🔴 Active period name:', active.name);
                }
            } catch (err) {
                console.error('Error fetching active period:', err);
            }

            // If no active period, use the first available period
            if (!active && periodsData.length > 0) {
                console.log('⚠️ No active period found, using first period:', periodsData[0]);
                active = periodsData[0];
            }

            console.log('✅ Final activePeriod set to:', active);
            setActivePeriod(active);
            setViewedPeriod(active);
            setSelectedPeriodId(active?.id || '');
            setIsLocked(active?.is_locked === 1);

            // Fetch org OKRs
            try {
                const orgResponse = await okrApi.getOrgOkrs(active?.id);
                console.log('🎯 Org OKRs raw response:', orgResponse);
                let orgData: OrgObjective[] = [];
                if (Array.isArray(orgResponse)) {
                    orgData = orgResponse;
                } else if (orgResponse && typeof orgResponse === 'object') {
                    orgData = Array.isArray((orgResponse as any).data) ? (orgResponse as any).data : [];
                }
                console.log('🎯 Org objectives loaded:', orgData.length, 'for period:', active?.id);
                setOrgObjectives(orgData);

                // Fetch retrospective notes for all org objectives
                if (orgData.length > 0) {
                    try {
                        const allNotes: Note[] = [];
                        for (const org of orgData) {
                            if (org?.id) {
                                console.log('📝 Fetching notes for org objective:', org.id);
                                const notesData = await okrApi.getNotes(org.id);
                                if (Array.isArray(notesData)) {
                                    allNotes.push(...notesData);
                                }
                            }
                        }
                        console.log('📝 Total notes received:', allNotes.length);
                        setNotes(allNotes);
                    } catch (notesErr: any) {
                        console.error('📝 Notes fetch error:', notesErr.message);
                    }
                }

                // Extract team objectives from org response
                const extractedTeamObjectives = orgData.flatMap((org: any) =>
                    (org.teamObjectives || []).map((t: any) => ({
                        ...t,
                        org_objective_id: org.id
                    }))
                );
                console.log('🎯 Team objectives extracted from org:', extractedTeamObjectives.length);
                setTeamObjectives(extractedTeamObjectives);
            } catch (err) {
                console.error('Error fetching org OKRs:', err);
                setOrgObjectives([]);
                setTeamObjectives([]);
            }

            // Fetch my OKRs
            try {
                const myResponse = await okrApi.getMyOkrs();
                const myData = Array.isArray(myResponse) ? myResponse : (myResponse as any)?.data || [];
                setMyObjectives(myData);
            } catch (err) {
                console.error('Error fetching my OKRs:', err);
                setMyObjectives([]);
            }

            // Fetch atomic map
            try {
                const atomicResponse = await okrApi.getAtomicMap();
                console.log('🗺️ Atomic Map Raw Response:', atomicResponse);
                const mapData = (atomicResponse as any)?.data || atomicResponse;
                console.log('🗺️ Atomic Map Data:', mapData);
                setAtomicMapData(mapData);
            } catch (err) {
                console.error('Error fetching atomic map:', err);
                setAtomicMapData(null);
            }

            // Fetch departments
            try {
                console.log('📦 Fetching departments via okrApi...');
                const departmentsData = await okrApi.getDepartments();
                console.log('📦 Departments received:', departmentsData);
                if (Array.isArray(departmentsData)) {
                    setDepartments(departmentsData);
                }
            } catch (err: any) {
                console.error('📦 Department fetch error:', err.message);
            }

            // Fetch employees for atomic map
            try {
                console.log('👥 Fetching employees via okrApi...');
                const employeesData = await okrApi.getEmployees();
                console.log('👥 Employees received:', employeesData);
                if (Array.isArray(employeesData)) {
                    setEmployees(employeesData);
                }
            } catch (err: any) {
                console.error('👥 Employee fetch error:', err.message);
            }

        } catch (err: any) {
            console.error('Error in fetchInitialData:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSeedPeriods = useCallback(async () => {
        try {
            setSeedingPeriods(true);
            await okrApi.seedPeriods();
            setSuccess('Periods seeded successfully');
            await fetchInitialData();
        } catch (err: any) {
            setError(err.message || 'Failed to seed periods');
        } finally {
            setSeedingPeriods(false);
        }
    }, [fetchInitialData]);

    const handleViewPeriod = useCallback(async (periodId: string) => {
        const period = periods.find(p => p.id === periodId);
        if (!period || period.id === viewedPeriod?.id) return;

        setViewedPeriod(period);
        setSelectedPeriodId(period.id);
        setIsLocked(period.is_locked === 1);

        // Load org OKRs (includes team objectives) for viewed period
        try {
            const orgResponse = await okrApi.getOrgOkrs(periodId);
            let orgData: OrgObjective[] = [];
            if (Array.isArray(orgResponse)) {
                orgData = orgResponse;
            } else if (orgResponse && typeof orgResponse === 'object') {
                orgData = Array.isArray((orgResponse as any).data) ? (orgResponse as any).data : [];
            }
            setOrgObjectives(orgData);

            const extractedTeamObjectives = orgData.flatMap((org: any) =>
                (org.teamObjectives || []).map((t: any) => ({
                    ...t,
                    org_objective_id: org.id
                }))
            );
            setTeamObjectives(extractedTeamObjectives);

            // Refetch notes for new org objectives
            if (orgData.length > 0) {
                const allNotes: Note[] = [];
                for (const org of orgData) {
                    if (org?.id) {
                        const notesData = await okrApi.getNotes(org.id);
                        if (Array.isArray(notesData)) {
                            allNotes.push(...notesData);
                        }
                    }
                }
                setNotes(allNotes);
            } else {
                setNotes([]);
            }
        } catch (err) {
            console.error('Error loading period data:', err);
        }
    }, [periods, viewedPeriod]);

    const handleSetActivePeriod = useCallback(async (periodId: string) => {
        try {
            await okrApi.setActivePeriod(periodId);
            setSuccess('Active period updated');
            await fetchInitialData();
        } catch (err: any) {
            setError(err.message || 'Failed to set active period');
        }
    }, [fetchInitialData]);

    const handleLockToggle = useCallback(async () => {
        if (!viewedPeriod) return;
        try {
            if (isLocked) {
                await okrApi.unlockPeriod(viewedPeriod.id);
                setIsLocked(false);
                setSuccess('Period reopened successfully');
            } else {
                await okrApi.lockPeriod(viewedPeriod.id);
                setIsLocked(true);
                setSuccess('Period closed. You can now create a new period.');
            }
            // Refetch periods to update states (e.g. hasOpenPeriod in DashboardHeader)
            const periodsResponse = await okrApi.getPeriods();
            const updatedPeriods = Array.isArray(periodsResponse) ? periodsResponse : [];
            setPeriods(updatedPeriods);
            // Update viewedPeriod from fresh data
            const fresh = updatedPeriods.find((p: Period) => p.id === viewedPeriod.id);
            if (fresh) setViewedPeriod(fresh);

            // Refetch OKR data (org objectives, my objectives, milestones) to reflect lock state
            try {
                const orgResponse = await okrApi.getOrgOkrs(viewedPeriod.id);
                let orgData: OrgObjective[] = [];
                if (Array.isArray(orgResponse)) {
                    orgData = orgResponse;
                } else if (orgResponse && typeof orgResponse === 'object') {
                    orgData = Array.isArray((orgResponse as any).data) ? (orgResponse as any).data : [];
                }
                setOrgObjectives(orgData);

                const extractedTeamObjectives = orgData.flatMap((org: any) =>
                    (org.teamObjectives || []).map((t: any) => ({
                        ...t,
                        org_objective_id: org.id
                    }))
                );
                setTeamObjectives(extractedTeamObjectives);

                const myResponse = await okrApi.getMyOkrs();
                const myData = Array.isArray(myResponse) ? myResponse : (myResponse as any)?.data || [];
                setMyObjectives(myData);

                const atomicResponse = await okrApi.getAtomicMap();
                const mapData = (atomicResponse as any)?.data || atomicResponse;
                setAtomicMapData(mapData);
            } catch (refetchErr) {
                console.error('Error refetching data after lock toggle:', refetchErr);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to toggle lock');
        }
    }, [viewedPeriod, isLocked]);

    const refetchData = useCallback(async () => {
        await fetchInitialData();
    }, [fetchInitialData]);

    // Soft refresh - refetch data without showing loading spinner
    const softRefetchData = useCallback(async () => {
        try {
            // Refetch org OKRs silently
            const orgResponse = await okrApi.getOrgOkrs(viewedPeriod?.id || activePeriod?.id);
            let orgData: OrgObjective[] = [];
            if (Array.isArray(orgResponse)) {
                orgData = orgResponse;
            } else if (orgResponse && typeof orgResponse === 'object') {
                orgData = Array.isArray((orgResponse as any).data) ? (orgResponse as any).data : [];
            }
            setOrgObjectives(orgData);

            // Extract team objectives from org response
            const extractedTeamObjectives = orgData.flatMap((org: any) =>
                (org.teamObjectives || []).map((t: any) => ({
                    ...t,
                    org_objective_id: org.id
                }))
            );
            setTeamObjectives(extractedTeamObjectives);

            // Refetch my OKRs silently
            try {
                const myResponse = await okrApi.getMyOkrs();
                const myData = Array.isArray(myResponse) ? myResponse : (myResponse as any)?.data || [];
                setMyObjectives(myData);
            } catch (err) {
                console.error('Error refetching my OKRs:', err);
            }

            // Refetch atomic map silently
            const atomicResponse = await okrApi.getAtomicMap();
            const mapData = (atomicResponse as any)?.data || atomicResponse;
            setAtomicMapData(mapData);
        } catch (err: any) {
            console.error('Error in softRefetchData:', err);
        }
    }, [viewedPeriod, activePeriod]);

    // Initial fetch on mount
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    return {
        // Loading & Error States
        loading,
        error,
        success,
        setError,
        setSuccess,

        // User Context
        userContext,
        setUserContext,

        // Period Data
        periods,
        activePeriod,
        viewedPeriod,
        selectedPeriodId,
        setSelectedPeriodId,
        isLocked,
        setIsLocked,

        // OKR Data
        orgObjectives,
        setOrgObjectives,
        teamObjectives,
        setTeamObjectives,
        myObjectives,
        setMyObjectives,

        // Supporting Data
        atomicMapData,
        setAtomicMapData,
        departments,
        setDepartments,
        employees,
        setEmployees,
        notes,
        setNotes,

        // Seeding State
        seedingPeriods,
        setSeedingPeriods,

        // Actions
        fetchInitialData,
        handleSeedPeriods,
        handleViewPeriod,
        handleSetActivePeriod,
        handleLockToggle,
        refetchData,
        softRefetchData,
    };
};

export default useOKRData;
